<template>
  <b-container
    class="commutator-general-tab"
    fluid
  >
    <div
      v-if="detailsLoading"
      class="my-5"
    >
      <s-loader
        class="text-center"
        size="lg"
      />
    </div>
    <div v-else-if="commutators.length">
      <b-row
        class="commutator-general-tab__details-row mb-2 avoid-break"
        v-for="commutatorId in tabInfo.commutatorsAmount"
        :key="commutatorId"
      >
        <commutator-details
          :commutator="getCommutator(commutatorId - 1)"
          :commutator-id="commutatorId"
          @inform-maintenance="openMaintenanceActionModal($event)"
          @input="collapseHandler($event, commutatorId)"
          @refresh-data="onCloseMaintenanceActionModal"
        />
      </b-row>
    </div>
    <s-empty-list v-else/>
    <b-row class="avoid-break">
      <accordion-panel
        id="last-commutations"
        class="commutator-general-tab__last-commutations avoid-break"
        :collapsed.sync="commutationsCollapsed"
      >
        <template #title>
          <strong>{{ $t('engineering.commutator-maintenance.last-commutations-count') }}</strong>
        </template>
        <div
          v-if="chartsLoading"
          class="my-5"
        >
          <s-loader
            class="text-center"
            size="lg"
          />
        </div>
        <div
          v-else-if="lastCommutations.length"
        >
          <b-row>
            <b-col
              cols="12"
              class="commutator-general-tab__subtitle-wrapper"
            >
              <div class="commutator-general-tab__subtitle-content"
                v-for="(subtitle, subtitleIndex) in subtitles"
                :key="subtitleIndex"
              >
                <div
                  class="commutator-general-tab__subtitle-box mr-2 mr-sm-3"
                  :class="subtitle.class"
                />
                <div>{{ $t('entities.commutator') }} {{ subtitle.text }}</div>
              </div>
            </b-col>
          </b-row>
          <b-row class="commutator-general-tab__chart-row">
            <b-col
              v-for="(commutation, index) in lastCommutations"
              class="mt-3"
              sm="4"
              md="4"
              xl="3"
              :key="index"
            >
              <commutators-chart
                :chart-data="commutation"
                :is-collapsed="commutationsCollapsed"
              />
            </b-col>
          </b-row>
        </div>
        <s-empty-list
          v-else
        />
      </accordion-panel>
    </b-row>
    <maintenance-action-modal
      v-model="showMaintenanceActionModal"
      :maintenance="maintenance"
      :is-loading="maintenanceModalLoading"
      @ok="onCloseMaintenanceActionModal"
    />
  </b-container>
</template>

<script>
import Service from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import globalEventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import AccordionPanel from '@/components/AccordionPanel';
import MaintenanceActionModal
  from '@/domain/components/installation/engineering/commutatorMaintenance/modal/MaintenanceActionModal';
import CommutatorDetails from './CommutatorDetails';
import CommutatorsChart from './CommutatorsChart';

export default {
  name: 'CommutatorMaintenanceGeneralTab',
  components: {
    CommutatorDetails,
    CommutatorsChart,
    AccordionPanel,
    MaintenanceActionModal,
  },
  props: {
    tabInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    lastCommutations: [],
    commutators: [],
    commutationCollapseStatus: null,
    chartsLoading: false,
    detailsLoading: false,
    maintenance: {},
    commutationsCollapsed: false,
    showMaintenanceActionModal: false,
    maintenanceModalLoading: false,
  }),
  computed: {
    assetModuleId() {
      return this.$route.params.assetModuleId;
    },
    subtitles() {
      const allSubtitles = [
        {
          class: 'gray',
          text: '1',
        },
        {
          class: 'green',
          text: '2',
        },
        {
          class: 'orange',
          text: '3',
        },
      ];
      const commutators = new Set();
      this.lastCommutations.forEach((commutation) => {
        (commutation.values || []).forEach((value) => {
          commutators.add(value.commutatorId);
        });
      });
      return allSubtitles.filter(subtitle => commutators.has(Number(subtitle.text)));
    },
  },
  created() {
    this.getCommutators(true);
    this.getLastCommutations(true);
    this.initListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    initListeners() {
      globalEventBus.on(events.refreshDataTab, this.refreshData);
    },
    removeListeners() {
      globalEventBus.off(events.refreshDataTab, this.refreshData);
    },
    refreshData(showLoading) {
      return Promise.allSettled([
        this.getLastCommutations(showLoading),
        this.getCommutators(showLoading),
      ]);
    },
    onCloseMaintenanceActionModal() {
      this.showMaintenanceActionModal = false;
      this.refreshData(false);
    },
    openMaintenanceActionModal(commutatorId) {
      this.getMaintenanceAction(commutatorId).then(() => {
        this.showMaintenanceActionModal = true;
      });
    },
    async getMaintenanceAction(commutatorId) {
      const { assetModuleId } = this.$route.params;
      this.maintenanceModalLoading = true;
      Service.getCommutatorMaintenanceAction(assetModuleId, commutatorId)
        .then((response) => {
          this.maintenance = response;
        })
        .finally(() => {
          this.maintenanceModalLoading = false;
        });
    },
    collapseHandler(value, index) {
      const commutator = this.commutators[index];
      if (!commutator) return;
      commutator.collapsed = value;
    },
    getCommutators(showLoading = false) {
      this.detailsLoading = showLoading;
      return Service.getCommutator(this.assetModuleId).then((data) => {
       this.commutators = data.map(it => ({ ...it, collapsed: false }));
      }).catch(() => {
        this.commutators = [];
      }).finally(() => {
        this.detailsLoading = false;
      });
    },
    getLastCommutations(showLoading = false) {
      this.chartsLoading = showLoading;
      return Service.getLastCommutations(this.assetModuleId).then((data) => {
        this.lastCommutations = data;
      }).catch(() => {
        this.lastCommutations = [];
      }).finally(() => {
        this.chartsLoading = false;
      });
    },
    getCommutator(index) {
      return this.commutators[index] || {};
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.commutator-general-tab
  &__subtitle-wrapper
    display: flex
  &__subtitle-content
    position: relative
    width: 25%
    display: flex
    align-items: center
  &__subtitle-box
    height: 15px
    width: 15px
  &__last-commutations
    border: 1px solid $color-border-gray
    border-radius: 5px
  &__chart-row
    width: 100%
    margin: 0
    > div:not(:first-child):not(:last-child)
      padding: 0 5px
    > div:first-child
      padding: 0 5px 0 0
    > div:last-child
      padding: 0 0 0 5px
  ::v-deep .accordion-panel
    margin-bottom: 2px
    box-shadow: none
    &__header, &__title-container
      display: flex
      align-items: center
      +media-min-md
        height: 30px
    &__header
      padding: 0 15px !important
.gray
  background: var(--color-gray)
.green
  background: var(--color-primary)
.orange
  background: var(--color-secondary)

@media screen
  +media-max-sm()
    .commutator-general-tab
      &__subtitle-content
        width: 33.333333333%

@media print
  .commutator-general-tab
    &__chart-row
      display: flex
      justify-content: space-between
      flex-wrap: nowrap
      max-width: 100%
      > div
        max-width: 25%
@-moz-document url-prefix()
  @media print and (orientation: portrait)
    .manage-fonts_18px
      .commutator-general-tab
        &__details-row:nth-child(3)
          height: 600px
  @media print and (orientation: landscape)
    .manage-fonts_18px
      .commutator-general-tab
        &__details-row:first-child
          height: 600px
    .commutator-general-tab
      &__details-row
        height: 320px

</style>
